import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as moment from 'moment';
import { auditTime, distinctUntilChanged, of, take } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { FileUploadsService } from '@app/services/file-uploads.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { MediaViewerComponent } from '@main-application/inspections/components/media-viewer/media-viewer.component';
import {
  CiteElementDisplayMode,
  ImageCaptureMode,
  InspectionItemStatus,
  RestInspectionContents,
} from '@main-application/inspections/models/rest-inspections-model.interface';
import { InspectionService } from '@main-application/inspections/services/inspection.service';
import {
  CustomDropDownOption,
  CustomFieldsService,
} from '@main-application/management/pages/property-model/services/custom-fields.service';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import {
  BooleanQuestion,
  CustomField,
  CustomFieldParsed,
  EmojiDetails,
  ImageDetails,
  RadioListOrientation,
  ResidentInspection,
  ResidentInspectionArea,
  ResidentInspectionItem,
} from '@main-application/resident/pages/inspect/inspect.model';
import { ResidentInspectionsService } from '@main-application/resident/services/resident-inspections.service';
import { TemplateEditorDialogModule } from '@main-application/shared/template-editor-dialog/template-editor-dialog.module';
import { ButtonMenuComponent } from '@shared/components/button/button-menu/button-menu.component';
import { allFileExtensions, imageVideoFileExtensions } from '@shared/constants/file-extensions.const';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { LocalStorageDataEnum } from '@shared/enums/local-storage-data.enum';
import { FileUploadModel } from '@shared/interfaces';
import { AttachmentItem } from '@shared/interfaces/attachment-item';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { PipesModule } from '@shared/pipes/pipes.module';
import { propertyStateValidator } from '@shared/validators/propertystate.validator';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { MultipleAttachmentsModule } from '@ui-components/components/multiple-attachments/multiple-attachments.module';
import { DateDropdownModule } from '@ui-components/controls/date-dropdown/date-dropdown.module';
import { InputModule } from '@ui-components/controls/input/input.module';
import { InputAreaModule } from '@ui-components/controls/input-area/input-area.module';
import { RadioButtonModule } from '@ui-components/controls/radio-button/radio-button.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';

interface ResidentInspectMappedImage {
  fileUploadId: number;
  cloudUri: string;
  thumbnailUri?: string | null;
  videoDuration?: number | null;
  originalFileName?: string | null;
  internalName?: string | null;
  isInspectionAttachment?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-resident-inspect-details',
  templateUrl: './resident-inspect-details.component.html',
  styleUrls: ['./resident-inspect-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    IconComponent,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    MatIconModule,
    TemplateEditorDialogModule,
    ButtonMenuComponent,
    MultipleAttachmentsModule,
    PipesModule,
    MatProgressSpinnerModule,
    InputModule,
    InputAreaModule,
    RadioButtonDropdownModule,
    RadioButtonModule,
    DateDropdownModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResidentInspectDetailsComponent extends PuSubscribable implements OnInit {
  cleanInputAttachment = new EventEmitter<void>();

  EColorPalette = EColorPalette;
  EIcon = EIcon;
  DisplayMode = CiteElementDisplayMode;
  ImageCaptureMode = ImageCaptureMode;
  RadioListOrientation = RadioListOrientation;

  inspectForm: FormGroup;
  dialogRef: MatDialogRef<any>;
  currentItemIndex = 0;
  totalItems: number;
  onSubmitOptions = [
    {
      label: 'Done & Next',
      id: false,
    },
    {
      label: 'Done',
      id: true,
    },
  ];
  onSubmitSelected = this.onSubmitOptions[0];
  inspectionId: number;
  inspectionContentId: number;
  isCommentRequired = false;
  isPhotoRequired = false;
  mappedImages = [];
  savedMappedImages = [];
  thumbnailPlaceholder = '../../../../../../../assets/icon/zoom-in.svg';
  isUploading = false;
  isSubmitting = false;
  hasChanges = false;
  isPhotoViewerOpened = false;
  currentArea: ResidentInspectionArea = null;
  currentAreaIndex = 0;
  initialEmojiDetails: EmojiDetails[] = [];
  initialFormData: any;
  allFileExtensions = allFileExtensions;
  imageVideoFileExtensions = imageVideoFileExtensions;
  private readonly today = this.timezoneService.getCurrentDateOnly();
  private savedAreaIds: number[] = [];
  skipLocalStorage = false;
  submitted = false;
  private initialInspectionAttachmentIdsMap = new Map<number, number[]>();

  @ViewChild('instructionsSheet') instructionsSheet: TemplateRef<any>;
  @ViewChild('storageConfirmSheet') storageConfirmSheet: TemplateRef<any>;

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_BOTTOM_SHEET_DATA)
    public data: {
      id: number;
      residentInspection: ResidentInspection;
      areaId: string;
      startIndex: number;
      guid?: string;
      disableStorage?: boolean;
      defaultSubmitOption?: 'Done' | 'Done & Next';
    },
    private dialog: MatDialog,
    private bottomSheetRef: MatBottomSheetRef<ResidentInspectDetailsComponent>,
    private cdr: ChangeDetectorRef,
    private inspectionService: InspectionService,
    private residentInspectionsService: ResidentInspectionsService,
    private fileUploadsService: FileUploadsService,
    private snackbarService: SnackbarService,
    public customFieldsService: CustomFieldsService,
    private timezoneService: TimezoneService
  ) {
    super();
  }

  ngOnInit(): void {
    this.onSubmitSelected = this.onSubmitOptions[this.data.defaultSubmitOption === 'Done & Next' ? 0 : 1];

    this.currentAreaIndex = this.data.residentInspection.areas.findIndex(area => area.id === this.data.id);
    this.currentArea = this.data.residentInspection.areas[this.currentAreaIndex];

    this.currentItemIndex = this.data.startIndex;
    this.totalItems = this.currentArea?.items?.length;

    this.initializeForm(this.currentArea?.items[this.currentItemIndex]);

    this.bottomSheetRef
      .backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.close();
      })
      .untilDestroyed(this);

    const instructionKey = this.getInstructionKey(this.currentArea.items[this.currentItemIndex]);
    if (
      !this.data.guid &&
      !this.isInstructionDismissed(instructionKey) &&
      this.currentArea.items[this.currentItemIndex].showInstructions &&
      this.currentArea.items[this.currentItemIndex].instructions
    ) {
      setTimeout(() => {
        this.openInstructions(this.currentArea.items[this.currentItemIndex].instructions);
      }, 300);
    }

    if (this.data.guid) {
      setTimeout(() => this.scrollToGuid(this.data.guid), 300);
    }
  }

  private initializeForm(data: ResidentInspectionItem) {
    this.inspectionId = data.inspectionId;
    this.inspectionContentId = data.id;
    this.initialEmojiDetails = data.emojiDetails ? [...data.emojiDetails] : [];

    if (!this.initialInspectionAttachmentIdsMap.has(data.id)) {
      this.initialInspectionAttachmentIdsMap.set(data.id, [...(data.fileUploadIdsInspectionContent ?? [])]);
    }

    if (this.inspectForm) this.inspectForm.reset();

    const allQuestions: { [guid: string]: FormGroup } = {};

    [
      ...this.createBooleanQuestions(data.booleanQuestions),
      ...this.createCustomQuestions(data.texts, 'text'),
      ...this.createCustomQuestions(data.numbers, 'number'),
      ...this.createCustomQuestions(data.ints, 'int'),
      ...this.createCustomQuestions(data.moneys, 'money'),
      ...this.createDateQuestions(data.dates),
      ...this.createDateTimeQuestions(data.dateTimes),
      ...this.createDropdownQuestions(data.dropdowns),
      ...this.createChecklistQuestions(data.checklists),
      ...this.createRadiolistQuestions(data.radiolists),
    ].forEach(q => {
      const guid = q.get('guid')?.value;
      if (guid) {
        allQuestions[guid] = q;
      }
    });

    const questions: FormGroup[] = [];
    if (Array.isArray(data.order)) {
      data.order
        .sort((a, b) => a.order - b.order)
        .forEach(o => {
          const q = allQuestions[o.guid];
          if (q) {
            questions.push(q);
          }
        });
    }

    this.inspectForm = this.fb.group({
      itemName: [data.itemName || '', Validators.required],
      comment: [data.comment || null],
      fileUploadIds: [data.fileUploadIds || []],
      emojiDetails: [this.initialEmojiDetails.map(emoji => ({ ...emoji })), propertyStateValidator],
      questions: this.fb.array(questions),
      imageCaptureMode: [data.imageCaptureMode],
      isPhotoVisible: [data.photoVisible ?? true],
      isCommentVisible: [data.commentVisible ?? true],
    });

    this.initialFormData = JSON.parse(JSON.stringify(this.inspectForm.getRawValue()));
    this.skipLocalStorage = false;

    this.mappedImages = [];
    this.loadFormDataFromLocalStorageOrProcessFileUploadIds(data);

    if (data.emojiDetails && data.emojiDetails?.length > 0) {
      this.setValidatorsBasedOnPropertyState();
    } else {
      this.setValidatorsWithoutPropertyState(data);
    }

    this.coerceDateControlsToDate();

    this.inspectForm.valueChanges
      .subscribe(() => {
        this.saveFormDataToLocalStorage(data);
      })
      .untilDestroyed(this);

    if (
      this.questions &&
      this.questions.value?.some(
        (question: CustomFieldParsed) =>
          question.type?.toLowerCase() === 'checklist' || question.type?.toLowerCase() === 'radiolist'
      )
    ) {
      this.questions.valueChanges
        .pipe(auditTime(50), distinctUntilChanged())
        .subscribe((questions: CustomFieldParsed[]) => {
          if (
            questions?.some(
              question => question.type?.toLowerCase() === 'checklist' || question.type?.toLowerCase() === 'radiolist'
            )
          ) {
            if (data.emojiDetails && data.emojiDetails?.length > 0) {
              this.setValidatorsBasedOnPropertyState();
            } else {
              this.setValidatorsWithoutPropertyState(data);
            }
          }
        })
        .untilDestroyed(this);
    }

    this.cdr.detectChanges();
  }

  public setEmojiDefault(index: number) {
    const emojis = this.inspectForm.get('emojiDetails').value;
    emojis.forEach((emoji: EmojiDetails, i: number) => {
      emoji.isDefault = i === index;
    });
    this.inspectForm.get('emojiDetails').setValue(emojis);
    this.inspectForm.get('emojiDetails').updateValueAndValidity();

    this.setValidatorsBasedOnPropertyState();
  }

  private setValidatorsWithoutPropertyState(data: ResidentInspectionItem) {
    const allChecklists = this.questions.value.filter(
      (question: CustomFieldParsed) =>
        question.type?.toLowerCase() === 'checklist' || question.type?.toLowerCase() === 'radiolist'
    );
    const { isCommentRequiredByCL, isPhotoRequiredByCL } = this.getValidatorsBasedOnChecklists(allChecklists);

    const isCommentVisible = this.inspectForm.get('isCommentVisible')?.value ?? true;
    const isPhotoVisible = this.inspectForm.get('isPhotoVisible')?.value ?? true;

    this.isCommentRequired = isCommentVisible && (data.commentRequired || isCommentRequiredByCL);
    this.isPhotoRequired = isPhotoVisible && (data.photoRequired || isPhotoRequiredByCL);

    const commentControl = this.inspectForm.get('comment');
    if (this.isCommentRequired) {
      commentControl.setValidators(Validators.required);
    } else {
      commentControl.clearValidators();
    }
    commentControl.updateValueAndValidity();

    const fileUploadIdsControl = this.inspectForm.get('fileUploadIds');
    if (this.isPhotoRequired) {
      fileUploadIdsControl.setValidators(Validators.required);
    } else {
      fileUploadIdsControl.clearValidators();
    }
    fileUploadIdsControl.updateValueAndValidity();

    this.cdr.detectChanges();
  }

  private setValidatorsBasedOnPropertyState() {
    const allChecklists = this.questions.value.filter(
      (question: CustomFieldParsed) =>
        question.type?.toLowerCase() === 'checklist' || question.type?.toLowerCase() === 'radiolist'
    );
    const { isCommentRequiredByCL, isPhotoRequiredByCL } = this.getValidatorsBasedOnChecklists(allChecklists);

    const emojis = this.inspectForm.get('emojiDetails').value;
    const selectedEmoji = emojis.find((emoji: EmojiDetails) => emoji.isDefault);

    const isCommentRequiredByEmoji = selectedEmoji?.onSelect?.includes('commentRequired') || false;
    const isPhotoRequiredByEmoji = selectedEmoji?.onSelect?.includes('photoRequired') || false;

    const isCommentVisible = this.inspectForm.get('isCommentVisible')?.value ?? true;
    const isPhotoVisible = this.inspectForm.get('isPhotoVisible')?.value ?? true;

    this.isCommentRequired = isCommentVisible && (isCommentRequiredByEmoji || isCommentRequiredByCL);
    this.isPhotoRequired = isPhotoVisible && (isPhotoRequiredByEmoji || isPhotoRequiredByCL);

    const commentControl = this.inspectForm.get('comment');
    if (this.isCommentRequired) {
      commentControl.setValidators(Validators.required);
    } else {
      commentControl.clearValidators();
    }
    commentControl.updateValueAndValidity();

    const fileUploadIdsControl = this.inspectForm.get('fileUploadIds');
    if (this.isPhotoRequired && this.mappedImages.length === 0) {
      fileUploadIdsControl.setValidators(Validators.required);
    } else {
      fileUploadIdsControl.clearValidators();
    }
    fileUploadIdsControl.updateValueAndValidity();

    this.cdr.detectChanges();
  }

  private getValidatorsBasedOnChecklists(allChecklists: CustomFieldParsed[]): {
    isCommentRequiredByCL: boolean;
    isPhotoRequiredByCL: boolean;
  } {
    const results = allChecklists.map((checklist: CustomFieldParsed) => {
      const values: string[] = Array.isArray(checklist.value)
        ? checklist.value
        : checklist.value
        ? [checklist.value]
        : [];

      return {
        isCommentRequired:
          (values.length > 0 && checklist.commentsRequiresForAllitems) ||
          values.some(v => checklist.commentsRequiresOn?.includes(v)),
        isPhotoRequired:
          (values.length > 0 && checklist.photoRequiresForAllitems) ||
          values.some(v => checklist.photoRequiresOn?.includes(v)),
      };
    });

    return {
      isCommentRequiredByCL: results.some(r => r.isCommentRequired),
      isPhotoRequiredByCL: results.some(r => r.isPhotoRequired),
    };
  }

  private createBooleanQuestions(questions: BooleanQuestion[] = []): FormGroup[] {
    return questions.map(question =>
      this.fb.group({
        type: ['boolean'],
        guid: [question.guid],
        question: [question.question, Validators.required],
        answer: [
          question.answer === 'true'
            ? true
            : question.answer === 'false'
            ? false
            : question.defaultAnswer === 'true'
            ? true
            : question.defaultAnswer === 'false'
            ? false
            : null,
          this.resolveRequiredValidators(question),
        ],
        defaultAnswer: [question.defaultAnswer === 'true' ? true : question.defaultAnswer === 'false' ? false : null],
        customFieldBinding: [question.customFieldBinding],
        isRequired: [this.resolveIsRequiredFlag(question)],
        displayMode: [question.displayMode ?? CiteElementDisplayMode.Value],
      })
    );
  }

  private createCustomQuestions(questions: CustomField[] = [], type: string): FormGroup[] {
    return questions.map(question =>
      this.fb.group({
        type: [type],
        guid: [question.guid],
        title: [question.title],
        defaultValue: [
          type === 'money'
            ? question.defaultValue != null
              ? this.customFieldsService.formatAmount(Number(question.defaultValue))
              : null
            : question.defaultValue,
        ],
        value: [
          type === 'money'
            ? question.value != null
              ? this.customFieldsService.formatAmount(Number(question.value))
              : question.defaultValue != null
              ? this.customFieldsService.formatAmount(Number(question.defaultValue))
              : null
            : question.value ?? question.defaultValue ?? null,
          this.resolveRequiredValidators(question),
        ],
        customFieldBinding: [question.customFieldBinding],
        isRequired: [this.resolveIsRequiredFlag(question)],
        displayMode: [question.displayMode ?? CiteElementDisplayMode.Value],
      })
    );
  }

  private createDateQuestions(dates: CustomField[] = []): FormGroup[] {
    return dates.map(question =>
      this.fb.group({
        type: ['date'],
        guid: [question.guid],
        title: [question.title],
        defaultValue: question.defaultValue ? moment(question.defaultValue).toDate() : null,
        value: [
          question.value
            ? moment(question.value).toDate()
            : question.defaultValue
            ? moment(question.defaultValue).toDate()
            : null,
          this.resolveRequiredValidators(question),
        ],
        customFieldBinding: [question.customFieldBinding],
        isRequired: [this.resolveIsRequiredFlag(question)],
        displayMode: [question.displayMode ?? CiteElementDisplayMode.Value],
      })
    );
  }

  private createDateTimeQuestions(dateTimes: CustomField[] = []): FormGroup[] {
    return dateTimes.map(question => {
      const m = question.value ? moment(question.value) : question.defaultValue ? moment(question.defaultValue) : null;

      const dateVal: Date | null = m?.isValid() ? m.toDate() : null;
      const timeVal: string | null = m?.isValid() ? m.toISOString() : null;

      const opts = this.customFieldsService
        .getDateTimes(dateVal)
        .map(o => ({ value: o.value.toISOString(), label: o.label }));

      const group = this.fb.group({
        type: this.fb.control<'dateTime'>('dateTime'),
        guid: this.fb.control(question.guid),
        title: this.fb.control(question.title),
        defaultValueDate: this.fb.control(question.defaultValue ? moment(question.defaultValue).toDate() : null),
        defaultValueTime: this.fb.control<string | null>(
          question.defaultValue ? moment(question.defaultValue).toISOString() : null
        ),
        valueDate: this.fb.control(dateVal, this.resolveRequiredValidators(question)),
        valueTime: this.fb.control<string | null>(timeVal, this.resolveRequiredValidators(question)),
        dateTimeOptions: this.fb.control(opts),
        customFieldBinding: this.fb.control(question.customFieldBinding),
        isRequired: this.fb.control(this.resolveIsRequiredFlag(question)),
        displayMode: this.fb.control(question.displayMode ?? CiteElementDisplayMode.Value),
      });

      group
        .get('valueDate')!
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe((newDate: Date | null) => {
          const newOpts = this.customFieldsService
            .getDateTimes(newDate)
            .map(o => ({ value: o.value.toISOString(), label: o.label }));
          group.get('dateTimeOptions')!.setValue(newOpts);

          const currentTime = group.get('valueTime')!.value as string | null;
          if (currentTime) {
            const mTime = moment(currentTime);
            if (mTime.isValid()) {
              const updated = moment(newDate)
                .set({ hour: mTime.hour(), minute: mTime.minute(), second: 0, millisecond: 0 })
                .toISOString();
              group.get('valueTime')!.setValue(updated, { emitEvent: false });
            }
          }
        })
        .untilDestroyed(this);

      return group;
    });
  }

  private createDropdownQuestions(dropdowns: CustomField[] = []): FormGroup[] {
    return dropdowns.map(field => {
      let groupedOptions: IRadioButtonOption<string>[][] = [];

      if (Array.isArray(field.options)) {
        const flatOptions: CustomDropDownOption[] = field.options.flat();

        const groupedMap = new Map<string | null, IRadioButtonOption<string>[]>();

        flatOptions.forEach(opt => {
          if (!opt) return;
          const group = opt.group ?? null;

          const item: IRadioButtonOption<string> = {
            value: opt.value ?? '',
            label: opt.label,
            ...(opt.color ? { color: opt.color } : {}),
          };

          if (!groupedMap.has(group)) groupedMap.set(group, []);
          groupedMap.get(group)!.push(item);
        });

        groupedOptions = Array.from(groupedMap.entries()).map(([group, items]) => {
          if (group && items?.length > 0) items.forEach(item => (item.groupLabel = group));
          return items;
        });
      }

      return this.fb.group({
        type: ['dropdown'],
        guid: [field.guid],
        title: [field.title],
        defaultValue: [field.defaultValue],
        value: [field.value ?? field.defaultValue, this.resolveRequiredValidators(field)],
        customFieldBinding: [field.customFieldBinding],
        options: [groupedOptions],
        isRequired: [this.resolveIsRequiredFlag(field)],
        displayMode: [field.displayMode ?? CiteElementDisplayMode.Value],
      });
    });
  }

  private createChecklistQuestions(checklists: CustomField[] = []): FormGroup[] {
    return checklists.map(field => {
      let groupedOptions: IRadioButtonOption<string>[][] = [];

      if (Array.isArray(field.options)) {
        const flatOptions: CustomDropDownOption[] = field.options.flat();

        const groupedMap = new Map<string | null, IRadioButtonOption<string>[]>();

        flatOptions.forEach(opt => {
          if (!opt) return;
          const group = opt.group ?? null;

          const item: IRadioButtonOption<string> = {
            value: opt.value ?? '',
            label: opt.label,
            ...(opt.color ? { color: opt.color } : {}),
          };

          if (!groupedMap.has(group)) groupedMap.set(group, []);
          groupedMap.get(group)!.push(item);
        });

        groupedOptions = Array.from(groupedMap.entries()).map(([group, items]) => {
          if (group && items?.length > 0) {
            items.forEach(item => (item.groupLabel = group));
          }
          return items;
        });
      }

      const parsedValue: string[] = field.value
        ? String(field.value)
            .split(',')
            .filter(v => v.trim() !== '')
        : [];

      const parsedDefault: string[] = field.defaultValue
        ? String(field.defaultValue)
            .split(',')
            .filter(v => v.trim() !== '')
        : [];

      const group = this.fb.group({
        type: ['checkList'],
        guid: [field.guid],
        title: [field.title],
        defaultValue: [parsedDefault],
        value: [parsedValue.length > 0 ? parsedValue : parsedDefault, this.resolveRequiredValidators(field)],
        customFieldBinding: [field.customFieldBinding],
        options: [groupedOptions],
        isRequired: [this.resolveIsRequiredFlag(field)],
        displayMode: [field.displayMode ?? CiteElementDisplayMode.Value],
        photoRequiresForAllitems: [field.photoRequiresForAllitems],
        commentsRequiresForAllitems: [field.commentsRequiresForAllitems],
        photoRequiresOn: [field.photoRequiresOn],
        commentsRequiresOn: [field.commentsRequiresOn],
      });

      return group;
    });
  }

  private createRadiolistQuestions(radiolists: CustomField[] = []): FormGroup[] {
    return radiolists.map(field => {
      const flatOptions: IRadioButtonOption<string>[] = (field.options ?? [])
        .flat()
        .filter(Boolean)
        .map(opt => ({
          value: opt.value,
          label: opt.label,
        }));

      return this.fb.group({
        type: ['radiolist'],
        guid: [field.guid],
        title: [field.title],
        defaultValue: [field.defaultValue],
        value: [field.value ?? field.defaultValue, this.resolveRequiredValidators(field)],
        customFieldBinding: [field.customFieldBinding],
        options: [flatOptions],
        isRequired: [this.resolveIsRequiredFlag(field)],
        displayMode: [field.displayMode ?? CiteElementDisplayMode.Value],
        photoRequiresForAllitems: [field.photoRequiresForAllitems],
        commentsRequiresForAllitems: [field.commentsRequiresForAllitems],
        photoRequiresOn: [field.photoRequiresOn],
        commentsRequiresOn: [field.commentsRequiresOn],
        orientation: [field.orientation],
      });
    });
  }

  private resolveIsRequiredFlag(entity: {
    isRequired?: boolean | null;
    customFieldBinding?: number | string | null;
  }): boolean {
    if (entity?.isRequired === true) {
      return true;
    }

    if (entity?.isRequired === false) {
      return false;
    }

    return entity?.customFieldBinding ? false : true;
  }

  private resolveRequiredValidators(entity: {
    isRequired?: boolean | null;
    customFieldBinding?: number | string | null;
  }): ValidatorFn[] {
    return this.resolveIsRequiredFlag(entity) ? [Validators.required] : [];
  }

  get questions(): FormArray {
    return this.inspectForm.get('questions') as FormArray;
  }

  public handleAttachment(attachments: AttachmentItem[]) {
    if (attachments?.length) {
      attachments.forEach(attachment => {
        const fileData = attachment.upload;
        this.mappedImages.push({
          cloudUri: fileData.cloudUri,
          fileUploadId: fileData.id,
          thumbnailUri: fileData.thumbnailUri || null,
          videoDuration: fileData.videoDurationInSeconds || null,
          originalFileName: fileData.originalFileName || null,
          internalName: fileData.internalName || null,
        });
      });

      Promise.resolve().then(() => {
        const key = this.residentInspectionsService.generateItemSpecificKey(
          this.currentArea.items[this.currentItemIndex]
        );
        localStorage.setItem(`${key}-images`, JSON.stringify(this.mappedImages));
      });

      this.setValidatorsBasedOnPropertyState();
      this.cleanInputAttachment.emit();
      this.cdr.detectChanges();
    }
  }

  public viewAllPhotos(images: ImageDetails[], isEditorMode = true, initialIndex = 0) {
    if (!images || images?.length === 0) {
      return;
    }

    const imageUrls = this.mappedImages.map(image => ({
      cloudUri: image.cloudUri,
      thumbnailFileUploadCloudUri: image.thumbnailUri,
      originalFileName: image.originalFileName,
      internalName: image.internalName,
    }));

    if (imageUrls?.length > 0) {
      const config: MatDialogConfig = {
        maxWidth: '100vw',
        minWidth: 'min(500px, 100vw)',
        height: '100vh',
        panelClass: 'photo-viewer-modal-container',
        data: {
          imageUrls,
          isEditorMode,
          initialIndex,
        },
      };

      this.isPhotoViewerOpened = true;
      const dialogRef = this.dialog.open(MediaViewerComponent, config);

      dialogRef
        .afterClosed()
        .subscribe(result => {
          this.isPhotoViewerOpened = false;
          if (result) {
            if (!result.deletedImages) return;
            const deletedImages = result.deletedImages || [];
            const deletedFileUploadIds = this.mappedImages
              .filter(image => deletedImages.includes(image.cloudUri))
              .map(image => image.fileUploadId);

            const updatedFileUploadIds = this.inspectForm
              .get('fileUploadIds')
              .value.filter((id: number) => !deletedFileUploadIds.includes(id));
            this.inspectForm.get('fileUploadIds').setValue(updatedFileUploadIds);

            this.mappedImages = this.mappedImages.filter(image => !deletedFileUploadIds.includes(image.fileUploadId));

            const key = this.residentInspectionsService.generateItemSpecificKey(
              this.currentArea.items[this.currentItemIndex]
            );
            localStorage.setItem(`${key}-images`, JSON.stringify(this.mappedImages));

            if (this.areFileUploadIdsEqual(this.mappedImages, this.savedMappedImages)) {
              localStorage.removeItem(`${key}-images`);
            }

            this.cdr.markForCheck();
          }
        })
        .untilDestroyed(this);
    }
  }

  public submitForm(isDone = false) {
    if (this.inspectForm.invalid) {
      this.submitted = true;
      setTimeout(() => {
        this.inspectForm.markAllAsTouched();
        setTimeout(() => {
          this.scrollToFirstInvalidControl();
          this.cdr.markForCheck();
        }, 50);
      }, 250);
      return;
    }
    this.submitted = true;
    if (!this.inspectForm.valid) return;

    this.hasChanges = true;

    const updatedData = this.inspectForm.value;
    const currentItem = this.currentArea.items[this.currentItemIndex];

    const allFileUploadIds = [
      ...new Set([...(updatedData.fileUploadIds || []), ...this.mappedImages.map(image => image.fileUploadId)]),
    ];

    currentItem.surveyJSON.pages.forEach((page, pageIndex) => {
      page.elements.forEach((element, elementIndex) => {
        const all = updatedData.questions;

        const updatedBoolean = all.find(q => q.type === 'boolean' && q.guid === element.guid);
        const updatedCustom = all.find(
          q =>
            ['text', 'number', 'int', 'dropdown', 'checklist', 'radiolist'].includes(q.type?.toLowerCase()) &&
            q.guid === element.guid
        );
        const updatedMoney = all.find(q => q.type === 'money' && q.guid === element.guid);
        const updatedDate = all.find(q => q.type === 'date' && q.guid === element.guid);
        const updatedDateTime = all.find(q => q.type?.toLowerCase() === 'datetime' && q.guid === element.guid);

        switch (element.type?.toLowerCase()) {
          case 'comment': {
            currentItem.surveyJSON.pages[pageIndex].elements[elementIndex].value = updatedData.comment;
            break;
          }

          case 'file': {
            currentItem.surveyJSON.pages[pageIndex].elements[elementIndex].mainImage = allFileUploadIds;
            break;
          }

          case 'boolean': {
            if (updatedBoolean) {
              currentItem.surveyJSON.pages[pageIndex].elements[elementIndex].value = String(updatedBoolean.answer);
            }
            break;
          }

          case 'text':
          case 'number':
          case 'int':
          case 'dropdown':
          case 'radiolist': {
            if (updatedCustom) {
              currentItem.surveyJSON.pages[pageIndex].elements[elementIndex].value = updatedCustom.value;
            }
            break;
          }

          case 'checklist': {
            if (updatedCustom) {
              const arr = Array.isArray(updatedCustom.value) ? updatedCustom.value : [];
              currentItem.surveyJSON.pages[pageIndex].elements[elementIndex].value =
                arr.length > 0 ? arr.join(',') : null;
            }
            break;
          }

          case 'money': {
            if (updatedMoney) {
              currentItem.surveyJSON.pages[pageIndex].elements[elementIndex].value = updatedMoney.value
                ? String(this.customFieldsService.parseAmount(updatedMoney.value))
                : null;
            }
            break;
          }

          case 'date': {
            if (updatedDate) {
              currentItem.surveyJSON.pages[pageIndex].elements[elementIndex].value = updatedDate.value
                ? moment(updatedDate.value).startOf('day').toISOString()
                : null;
            }
            break;
          }

          case 'datetime': {
            if (updatedDateTime) {
              const date: Date = updatedDateTime.valueDate;
              const time: string = updatedDateTime.valueTime;

              let merged: string | null = null;
              if (date && time) {
                const [hour, minute] = time.split(':').map(Number);
                merged = moment(date).set({ hour: hour, minute: minute }).toISOString();
              }
              currentItem.surveyJSON.pages[pageIndex].elements[elementIndex].value = merged;
            }
            break;
          }
        }
      });
    });

    this.currentArea.items[this.currentItemIndex] = {
      ...currentItem,
      emojiDetails: updatedData.emojiDetails,
      comment: updatedData.comment,
      fileUploadIds: allFileUploadIds,
      surveyJSON: currentItem.surveyJSON,

      booleanQuestions: updatedData.questions.filter(q => q.type === 'boolean'),
      texts: updatedData.questions.filter(q => q.type === 'text'),
      numbers: updatedData.questions.filter(q => q.type === 'number'),
      ints: updatedData.questions.filter(q => q.type === 'int'),
      moneys: updatedData.questions.filter(q => q.type === 'money'),
      dates: updatedData.questions.filter(q => q.type === 'date'),
      dateTimes: updatedData.questions.filter(q => q.type === 'dateTime'),
      dropdowns: updatedData.questions.filter(q => q.type === 'dropdown'),
      radiolists: updatedData.questions.filter(q => q.type === 'radiolist'),
    };

    const answer = this.residentInspectionsService.generateAnswer(
      this.currentArea.items,
      this.currentArea.items[this.currentItemIndex].uuid
    );
    const isCompleted = this.currentArea.items.every((item: ResidentInspectionItem) =>
      this.residentInspectionsService.getCompletedItem(item)
    );

    const payload = {
      id: this.currentArea.id,
      inspectionId: this.currentArea.inspectionId,
      inspectionTemplateAreaId: this.currentArea.inspectionTemplateAreaId,
      answerJSON: answer,
      status: isCompleted ? InspectionItemStatus.Completed : InspectionItemStatus.Incompleted,
    } as RestInspectionContents;

    const contentElement = document.querySelector('.inspect-form-content');

    const inspectionAttachmentIds = this.getInspectionAttachmentIds();
    const initialInspectionAttachmentIds = this.initialInspectionAttachmentIdsMap.get(currentItem.id) ?? [];
    const inspectionAttachmentIdsToDelete = initialInspectionAttachmentIds.filter(
      id => !inspectionAttachmentIds.includes(id)
    );

    const removal$ = inspectionAttachmentIdsToDelete.length
      ? this.inspectionService
          .removeInspectionAttachments(
            currentItem.inspectionId,
            this.currentArea.id,
            this.getCurrentPageIndex(),
            inspectionAttachmentIdsToDelete
          )
          .pipe(take(1))
      : of(null);

    const save$ = removal$.pipe(
      tap(() => {
        this.initialInspectionAttachmentIdsMap.set(currentItem.id, [...inspectionAttachmentIds]);
      }),
      switchMap(() => this.inspectionService.queueSubmitAnswers(payload).pipe(take(1)))
    );

    const runFade = () => {
      if (!contentElement) return;
      contentElement.classList.add('fade-out');
      setTimeout(() => {
        contentElement.classList.remove('fade-out');
      }, 400);
    };

    this.skipLocalStorage = true;

    if (isDone) {
      this.isSubmitting = true;
      this.cdr.detectChanges();

      save$
        .subscribe({
          next: () => {
            runFade();

            this.clearFormDataFromLocalStorage(this.currentArea.items[this.currentItemIndex]);

            if (!this.savedAreaIds.includes(this.currentArea.id)) {
              this.savedAreaIds.push(this.currentArea.id);
            }

            this.bottomSheetRef.dismiss({
              result: this.hasChanges,
              savedAreaIds: this.savedAreaIds,
            });
          },
          error: err => {
            this.snackbarService.error('Failed to save. Please try again.');
            console.error(err);
            this.cdr.markForCheck();
          },
        })
        .untilDestroyed(this);
    } else {
      const prevArea = this.currentArea;

      this.clearFormDataFromLocalStorage(this.currentArea.items[this.currentItemIndex]);

      if (!this.savedAreaIds.includes(prevArea.id)) {
        this.savedAreaIds.push(prevArea.id);
      }

      save$
        .subscribe({
          error: err => {
            this.snackbarService.error('Failed to save the previous item. Please try again.');
            console.error(err);
            this.cdr.markForCheck();
          },
        })
        .untilDestroyed(this);

      runFade();

      if (this.currentItemIndex === this.totalItems - 1) {
        if (this.currentAreaIndex < this.data.residentInspection.areas?.length - 1) {
          this.currentAreaIndex++;
          this.currentArea = this.data.residentInspection.areas[this.currentAreaIndex];
          this.currentItemIndex = 0;
          this.totalItems = this.currentArea.items.length;
          this.initializeForm(this.currentArea.items[this.currentItemIndex]);
        }
      } else {
        this.currentItemIndex++;
        this.initializeForm(this.currentArea.items[this.currentItemIndex]);
      }
    }
  }

  private scrollToFirstInvalidControl() {
    const firstInvalidControl = document.querySelector('form .ng-invalid') as HTMLElement;

    if (firstInvalidControl) {
      const scrollContainer = firstInvalidControl.closest('.content') as HTMLElement;

      if (scrollContainer) {
        const elTop = firstInvalidControl.offsetTop - scrollContainer.offsetTop;
        scrollContainer.scrollTo({
          top: elTop - 28,
          behavior: 'smooth',
        });
      }

      if (typeof firstInvalidControl.focus === 'function') {
        firstInvalidControl.focus();
      }
    }
  }

  private scrollToGuid(guid: string) {
    const el: HTMLElement = document.querySelector(`[guid="${guid}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.focus();
    }
  }

  public markAs(id: boolean): void {
    this.onSubmitSelected = this.onSubmitOptions.find(el => el.id === id);
  }

  public close(): void {
    if (this.isUploading) {
      this.snackbarService.error('Please wait until the file uploads are complete.');
      return;
    }

    if (this.data?.disableStorage) {
      const item = this.currentArea.items[this.currentItemIndex];
      const key = this.residentInspectionsService.generateItemSpecificKey(item);
      const hasFormData = !!localStorage.getItem(key);
      const hasImagesData = !!localStorage.getItem(`${key}-images`);

      if (hasFormData || hasImagesData) {
        this.dialogRef = this.dialog.open(this.storageConfirmSheet, {
          hasBackdrop: true,
          autoFocus: false,
          panelClass: 'instructions-container',
          position: { bottom: '0px' },
          width: '100vw',
          minWidth: '100vw',
          maxWidth: '100vw',
        });
        return; // don't close yet
      }
    }

    this.bottomSheetRef.dismiss({
      result: this.hasChanges,
      savedAreaIds: this.savedAreaIds,
    });
  }

  public confirmDiscard(): void {
    const item = this.currentArea.items[this.currentItemIndex];
    this.clearFormDataFromLocalStorage(item);
    if (this.dialogRef) this.dialogRef.close();
    this.bottomSheetRef.dismiss({
      result: this.hasChanges,
      savedAreaIds: this.savedAreaIds,
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onEscapeKeyDown(event: KeyboardEvent): void {
    if (!this.isPhotoViewerOpened) {
      this.close();
    }
  }

  public openInstructions(instructions: string) {
    this.dialogRef = this.dialog.open(this.instructionsSheet, {
      hasBackdrop: true,
      autoFocus: false,
      panelClass: 'instructions-container',
      position: { bottom: '0px' },
      width: '100vw',
      minWidth: '100vw',
      maxWidth: '100vw',
      data: { instructions },
    });
  }

  public closeInstructions(doNotShowAgain = false): void {
    if (doNotShowAgain) {
      const instructionKey = this.getInstructionKey(this.currentArea.items[this.currentItemIndex]);
      this.markInstructionAsDismissed(instructionKey);
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private getInstructionKey(item: ResidentInspectionItem): string {
    return `${LocalStorageDataEnum.INSTRUCTIONS_DISMISSED}-${item.id}-${item.inspectionId}-${item.inspectionTemplateAreaId}-${item.uuid}`;
  }

  private isInstructionDismissed(key: string): boolean {
    return localStorage.getItem(key) === 'true';
  }

  private markInstructionAsDismissed(key: string): void {
    localStorage.setItem(key, 'true');
  }

  private saveFormDataToLocalStorage(item: ResidentInspectionItem) {
    if (this.skipLocalStorage) return;

    const formData = this.inspectForm.getRawValue();
    const isEqualToInitial = this.deepEqual(this.initialFormData, formData);

    if (!isEqualToInitial) {
      const key = this.residentInspectionsService.generateItemSpecificKey(item);
      localStorage.setItem(key, JSON.stringify(formData));
    } else {
      this.clearFormDataFromLocalStorage(item);
    }
  }

  private loadFormDataFromLocalStorageOrProcessFileUploadIds(item: ResidentInspectionItem) {
    const key = this.residentInspectionsService.generateItemSpecificKey(item);
    const storedData = localStorage.getItem(key);
    const storedImages = localStorage.getItem(`${key}-images`);

    if (storedImages) {
      this.mappedImages = JSON.parse(storedImages);
      const fileUploadIds = this.inspectForm.get('fileUploadIds').value;
      this.savedMappedImages = this.mappedImages.filter(image => fileUploadIds.includes(image.fileUploadId));
    } else {
      this.mappedImages = [];
    }

    if (storedData) {
      const revived = this.reviveDataFromLocalStorage(storedData);

      if (this.isLocalStorageDataCorrupted(revived)) {
        this.clearFormDataFromLocalStorage(item);
        this.processFileUploadIds(item.fileUploadIds, item);
      } else {
        if (revived.dropdowns) {
          revived.dropdowns.forEach((dd: any) => {
            if (dd.options && dd.options.length) {
              dd.options = dd.options.map((group: any[]) =>
                group.map(opt => ({
                  value: opt.value,
                  label: opt.label,
                  groupLabel: opt.groupLabel ?? null,
                  ...(opt.color ? { color: opt.color } : {}),
                }))
              );
            }
          });
        }
        this.inspectForm.patchValue(revived, { emitEvent: false });
        this.processFileUploadIds(revived.fileUploadIds, item);
      }
    } else {
      this.processFileUploadIds(item.fileUploadIds, item);
    }

    this.cdr.detectChanges();
  }

  private reviveDataFromLocalStorage(json: string): any {
    const parsed = JSON.parse(json);

    const fixDates = (arr: any[], keys: string[]) => {
      if (!Array.isArray(arr)) return;
      arr.forEach(item => {
        keys.forEach(k => {
          if (item[k]) {
            const d = moment(item[k]).isValid() ? moment(item[k]).toDate() : null;
            item[k] = isNaN(d.getTime()) ? null : d;
          }
        });
      });
    };

    fixDates(parsed.dates, ['value', 'defaultValue']);
    fixDates(parsed.dateTimes, [
      'value',
      'defaultValue',
      'valueDate',
      'valueTime',
      'defaultValueDate',
      'defaultValueTime',
    ]);

    if (Array.isArray(parsed.dropdowns)) {
      parsed.dropdowns.forEach(dd => {
        if (Array.isArray(dd.options)) {
          dd.options = dd.options.map(group =>
            Array.isArray(group)
              ? group.map(opt => ({
                  value: opt.value,
                  label: opt.label,
                  groupLabel: opt.groupLabel ?? null,
                  ...(opt.color ? { color: opt.color } : {}),
                }))
              : []
          );
        }
      });
    }

    return parsed;
  }

  private coerceDateControlsToDate(): void {
    const toDate = (v: any) => (v ? (moment(v).isValid() ? moment(v).toDate() : null) : null);

    this.questions.controls
      .filter(ctrl => ctrl.get('type')?.value === 'date')
      .forEach(ctrl => {
        const v = ctrl.get('value');
        v?.setValue(toDate(v?.value), { emitEvent: false });
        const dv = ctrl.get('defaultValue');
        dv?.setValue(toDate(dv?.value), { emitEvent: false });
      });

    this.questions.controls
      .filter(ctrl => ctrl.get('type')?.value === 'dateTime')
      .forEach(ctrl => {
        const vd = ctrl.get('valueDate');
        vd?.setValue(toDate(vd?.value), { emitEvent: false });
        const vt = ctrl.get('valueTime');
        vt?.setValue(vt?.value ?? null, { emitEvent: false });
        const dvd = ctrl.get('defaultValueDate');
        dvd?.setValue(toDate(dvd?.value), { emitEvent: false });
        const dvt = ctrl.get('defaultValueTime');
        dvt?.setValue(dvt?.value ?? null, { emitEvent: false });
      });
  }

  private isLocalStorageDataCorrupted(storedData: any): boolean {
    const initialEmojis = this.initialFormData.emojiDetails;
    const storedEmojis = storedData.emojiDetails;
    if (initialEmojis?.length !== storedEmojis?.length) {
      return true;
    }

    const initialQuestions = this.initialFormData.booleanQuestions;
    const storedQuestions = storedData.booleanQuestions;
    if (initialQuestions?.length !== storedQuestions?.length) {
      return true;
    }

    if (!storedData.itemName || !storedData.fileUploadIds) {
      return true;
    }

    return false;
  }

  private processFileUploadIds(fileUploadIds: number[], item: ResidentInspectionItem) {
    if (!fileUploadIds?.length || fileUploadIds?.length === 0) return;
    const idsToFetch = fileUploadIds.filter(id => !this.mappedImages.some(img => img.fileUploadId === id));
    if (idsToFetch.length === 0) return;

    this.fileUploadsService
      .search({
        ids: idsToFetch,
        inspectionId: item.inspectionId,
        inspectionContentId: item.id,
      })
      .subscribe(result => {
        if (result?.length) {
          result.forEach((fileData: FileUploadModel) => {
            const imageData = {
              fileUploadId: fileData.id,
              cloudUri: fileData.cloudUri,
              thumbnailUri: fileData.thumbnailFileUploadCloudUri || null,
              videoDuration: fileData.videoDurationInSeconds || null,
              originalFileName: fileData.originalFileName || null,
              internalName: fileData.internalName || null,
              isInspectionAttachment: item.fileUploadIdsInspectionContent?.includes(fileData.id) ?? false,
            };
            this.mappedImages.push({ ...imageData });
          });

          this.savedMappedImages = JSON.parse(JSON.stringify(this.mappedImages));
          this.cdr.markForCheck();
        }
      })
      .untilDestroyed(this);
  }

  private getCurrentItem(): ResidentInspectionItem {
    return this.currentArea.items[this.currentItemIndex];
  }

  private getCurrentPageIndex(): number {
    return this.getCurrentItem()?.inspectionTemplateAreaId;
  }

  private getInspectionAttachmentIds(): number[] {
    return Array.from(
      new Set(
        this.mappedImages
          .filter(image => image.isInspectionAttachment)
          .map(image => image.fileUploadId)
          .filter(id => id !== null && id !== undefined)
      )
    );
  }

  private clearFormDataFromLocalStorage(item: ResidentInspectionItem) {
    const key = this.residentInspectionsService.generateItemSpecificKey(item);
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}-images`);
  }

  public userClearFormDataFromLocalStorage(item: ResidentInspectionItem): void {
    const hadErrorsVisible = this.inspectForm?.touched;

    const key = this.residentInspectionsService.generateItemSpecificKey(item);
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}-images`);

    this.initializeForm(item);

    if (hadErrorsVisible && this.inspectForm.invalid) {
      this.inspectForm.markAllAsTouched();
      this.cdr.markForCheck();
    }
  }

  private deepEqual(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  private areFileUploadIdsEqual(arr1: any[], arr2: any[]): boolean {
    const fileUploadIds1 = arr1.map(image => image.fileUploadId).sort();
    const fileUploadIds2 = arr2.map(image => image.fileUploadId).sort();

    return JSON.stringify(fileUploadIds1) === JSON.stringify(fileUploadIds2);
  }

  public hasUnsavedData(item: ResidentInspectionItem): boolean {
    const localStorageKey = this.residentInspectionsService.generateItemSpecificKey(item);
    const hasFormData = !!localStorage.getItem(localStorageKey);
    const hasImagesData = !!localStorage.getItem(`${localStorageKey}-images`);

    return hasFormData || hasImagesData;
  }

  isImage(file: ImageDetails): boolean {
    if (file?.thumbnailUri && file?.videoDuration) return true;
    if (!file?.thumbnailUri && !file?.cloudUri) return false;

    const cleanUrl = file?.cloudUri?.split(/[?#]/)[0];
    const ext = cleanUrl?.split('.').pop()?.toLowerCase();
    if (!ext) return false;

    const imageExtensions = new Set([
      'jpg',
      'jpeg',
      'jpe',
      'jif',
      'jfif',
      'jfi',
      'png',
      'gif',
      'bmp',
      'dib',
      'tif',
      'tiff',
      'webp',
      'svg',
      'heic',
      'heif',
      'ico',
      'cur',
      'avif',
    ]);

    return imageExtensions.has(ext);
  }

  public getExtension(file: ImageDetails): string {
    const cleanUrl = file?.cloudUri.split(/[?#]/)[0];
    const extension = cleanUrl.split('.').pop()?.toUpperCase();
    return extension ?? 'file';
  }

  public arraysEqual(a: any[] = [], b: any[] = []): boolean {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;

    const normalize = (arr: any[]) => arr.map(v => (v != null ? String(v).trim() : '')).sort();

    const sortedA = normalize(a);
    const sortedB = normalize(b);

    return sortedA.every((val, i) => val === sortedB[i]);
  }

  public getChecklistDefaultLabels(question: any): string {
    const options = question.get('options')?.value || [];
    const defaultValues: string[] = question.get('defaultValue')?.value || [];

    if (!Array.isArray(defaultValues) || defaultValues.length === 0) {
      return '';
    }

    const labels: string[] = [];
    options.forEach((group: any[]) => {
      group.forEach(opt => {
        if (defaultValues.includes(opt.value)) {
          labels.push(opt.label);
        }
      });
    });

    return labels.join(', ');
  }

  public getRadiolistDefaultLabels(question: any): string {
    const options = question.get('options')?.value || [];
    const defaultValue = question.get('defaultValue')?.value;

    if (!defaultValue) {
      return '';
    }

    const defaultValues = Array.isArray(defaultValue) ? defaultValue : [defaultValue];

    const labels = options.filter((opt: any) => defaultValues.includes(opt.value)).map((opt: any) => opt.label);

    return labels.join(', ');
  }

  public hasChanged(question: any): boolean {
    const value = question.get('value')?.value;
    const defaultValue = question.get('defaultValue')?.value;

    const valArray = Array.isArray(value) ? value : [value];
    const defArray = Array.isArray(defaultValue) ? defaultValue : [defaultValue];

    if (valArray.length !== defArray.length) return true;
    return !valArray.every(v => defArray.includes(v));
  }
}
